import classNames from 'classnames';
import { StaticImage } from 'gatsby-plugin-image';
import { Fragment } from 'react';

import { Hours, Phone } from '~components/AllianceCallService/AllianceCallService';
import Button from '~components/Button/Button';
import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import { prefixImageLink, redirectToEhmp } from '~globals/common';
import { fireTrackEvent } from '~globals/tracking';
import { useAppSelector } from '~hooks/useRedux';

type Props = {
    title: string;
    body?: string;
    ctacopy?: string;
    mediaurl?: string;
    shopoption: string;
};

const WayToShop = ({ title, body, ctacopy, mediaurl, shopoption }: Props) => {
    const allid = useAppSelector((state) => state.global.allianceId);
    const countyName = useAppSelector((state) => state.global.geoLocation.countyName);
    const leadToken = useAppSelector((state) => state.global.leadToken);
    const ntmVisited = useAppSelector((state) => state.global.ntmVisited);
    const sessionCode = useAppSelector((state) => state.user.savedSession.sessionCode);
    const trackingParams = useAppSelector((state) => state.global.trackingParams);
    const zip = useAppSelector((state) => state.global.geoLocation.zip);
    const throttle = useAppSelector((state) => state.global.throttle);
    const trackingComponentName = 'Ways to Enroll';

    const annualCostData = usePageContext().annualCostData.customProperties;

    const insertPlanYear = (copy: string) => {
        return copy.replaceAll('{{CURRENT_YEAR}}', annualCostData.CURRENT_YEAR.value);
    };

    const trackScheduleCallClick = () => {
        fireTrackEvent({ page: { action: `${trackingComponentName}: schedule call` } });
    };

    const handleShoppingRedirect = () => {
        fireTrackEvent({ page: { action: `${trackingComponentName}: See Plans` } });
        redirectToEhmp({
            ...trackingParams,
            allid: allid,
            zip: zip,
            leadToken: leadToken,
            county: countyName,
            action: 'redirect_with_zip_code',
            lastV1Action: 'quoteFromV1',
            ...(ntmVisited.length > 0 && { n2m: 'true' }),
            ...(sessionCode && { ppSaveSessionId: sessionCode }),
        });
    };

    const renderDefaultStaticImage = () => {
        const imgAttrs = { alt: '', className: 'w-[248px] h-[248px] rounded-full mx-auto' };
        switch (shopoption) {
            case 'speak':
                return <StaticImage src="./images/call-advisor.svg" {...imgAttrs} />;
            case 'appointment':
                return <StaticImage src="./images/schedule-advisor.svg" {...imgAttrs} />;
            default:
                return <StaticImage src="./images/chat-advisor.svg" {...imgAttrs} />;
        }
    };

    return (
        <section
            className={classNames(
                'text-center flex-1 lg:flex lg:flex-col lg:justify-between',
                shopoption === 'speak' && throttle && throttle !== 'GREEN' && 'hidden'
            )}
        >
            <div>
                {mediaurl ? (
                    <picture>
                        <img
                            src={prefixImageLink(mediaurl)}
                            alt=""
                            className="w-[248px] h-[248px] rounded-full mx-auto"
                        />
                    </picture>
                ) : (
                    renderDefaultStaticImage()
                )}
                <p className="text-24 leading-32 font-poppins font-bold text-link-visited mt-6 mb-2.5">
                    {insertPlanYear(title)}
                </p>
                <p className="text-16 leading-24 text-center text-link-visited pb-5 lg:text-18 lg:leading-28">
                    {body}
                    {shopoption === 'speak' && (
                        <Fragment>
                            <br />
                            <Hours />
                        </Fragment>
                    )}
                </p>
            </div>
            {shopoption === 'speak' && (
                <Fragment>
                    <div className="hidden lg:block">
                        <Phone
                            classname="text-24 leading-32 font-poppins font-bold text-primary"
                            fromComponentName={trackingComponentName}
                        />
                        <span className="text-black pl-2.5">TTY 711</span>
                    </div>
                    <div className="block lg:hidden">
                        <Phone
                            isbtn="secondary"
                            classname="w-full lg:w-auto"
                            fromComponentName={trackingComponentName}
                        />
                        <p className="text-20 leading-28 font-poppins font-bold pt-2">TTY 711</p>
                    </div>
                </Fragment>
            )}
            {shopoption === 'compare-plans' && (
                <Button
                    styletype="secondary"
                    className="w-full lg:w-max mx-auto"
                    onClick={() => handleShoppingRedirect()}
                >
                    {ctacopy}
                </Button>
            )}
            {shopoption === 'appointment' && (
                <Button
                    styletype="secondary"
                    className="w-full lg:w-max mx-auto"
                    islink={true}
                    to="/schedule-appointment/#guided-shopping/online_apt_prod_type"
                    onClick={trackScheduleCallClick}
                >
                    {ctacopy}
                </Button>
            )}
        </section>
    );
};

export default WayToShop;
